import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useState } from 'react';
import {
  useDeleteBusinessUnitAdMutation,
  useGetAllBusinessUnitAdsQuery,
  useUpdateBusinessUnitAdMutation,
} from 'services/api/businessUnitAds.api';
import type { BusinessUnitAd, BusinessUnitAdUpdate } from 'services/types/businessUnitAd';

interface BusinessUnitAdsProps {
  businessUnitId: number;
}

const BusinessUnitAds: React.FC<BusinessUnitAdsProps> = ({ businessUnitId }) => {
  const [editingRow, setEditingRow] = useState<BusinessUnitAdUpdate | null>(null);
  const [error, setError] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { data: businessUnitAds, isLoading, refetch } = useGetAllBusinessUnitAdsQuery(businessUnitId);
  const [updateBusinessUnitAd, { isLoading: isUpdating }] = useUpdateBusinessUnitAdMutation();
  const [deleteBusinessUnitAd, { isLoading: isDeleting }] = useDeleteBusinessUnitAdMutation();

  // Filter ads based on search query
  const filteredAds = businessUnitAds?.filter((ad) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    return (
      ad.adSourceId?.toLowerCase().includes(query) ||
      ad.adSource?.toLowerCase().includes(query) ||
      ad.campaignSourceName?.toLowerCase().includes(query) ||
      ad.campaignExternalCRMId?.toLowerCase().includes(query) ||
      ad.productOfInterest?.toLowerCase().includes(query)
    );
  });

  const handleEditClick = (ad: BusinessUnitAd) => {
    setError('');
    setEditingRow({
      id: ad.id,
      campaignSourceName: ad.campaignSourceName || '',
      campaignExternalCRMId: ad.campaignExternalCRMId || '',
      productOfInterest: ad.productOfInterest || '',
    });
  };

  const handleCancelEdit = () => {
    setError('');
    setEditingRow(null);
  };

  const handleSaveEdit = () => {
    if (!editingRow) return;
    updateBusinessUnitAd({
      id: editingRow.id,
      campaignSourceName: editingRow.campaignSourceName || null,
      campaignExternalCRMId: editingRow.campaignExternalCRMId || null,
      productOfInterest: editingRow.productOfInterest || null,
    })
      .then(() => {
        setEditingRow(null);
        setError('');
        refetch().catch(() => {
          // Refetch error is not critical, silently ignore
        });
      })
      .catch((err) => {
        console.error('Error updating business unit ad:', err);
        setError('Error updating business unit ad');
      });
  };

  const handleEditingFieldChange = (field: keyof BusinessUnitAdUpdate, value: string) => {
    if (!editingRow) return;
    setEditingRow({ ...editingRow, [field]: value });
  };

  const handleDeleteAd = (id: number) => {
    if (window.confirm('Are you sure you want to delete this ad?')) {
      deleteBusinessUnitAd(id)
        .then(() => {
          refetch().catch(() => {
            // Refetch error is not critical, silently ignore
          });
        })
        .catch((err) => {
          console.error('Error deleting ad:', err);
          setError('Error deleting ad');
        });
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6">Business Unit Ads</Typography>
      </Box>
      <Box
        sx={{
          mb: 2,
        }}
      >
        <TextField
          placeholder="Search ads..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ minWidth: 300 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      {error && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
          <Typography variant="body2">Error: {error}</Typography>
        </Paper>
      )}
      <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Ad Source ID</TableCell>
              <TableCell>Ad Source</TableCell>
              <TableCell>Campaign Source Name</TableCell>
              <TableCell>Campaign External CRM ID</TableCell>
              <TableCell>Product of Interest</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAds?.map((ad) => (
              <TableRow key={ad.id}>
                <TableCell>{ad.adSourceId}</TableCell>
                <TableCell>{ad.adSource}</TableCell>
                <TableCell>
                  {editingRow?.id === ad.id ? (
                    <TextField
                      value={editingRow.campaignSourceName}
                      onChange={(e) => handleEditingFieldChange('campaignSourceName', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  ) : (
                    ad.campaignSourceName || '-'
                  )}
                </TableCell>
                <TableCell>
                  {editingRow?.id === ad.id ? (
                    <TextField
                      value={editingRow.campaignExternalCRMId}
                      onChange={(e) => handleEditingFieldChange('campaignExternalCRMId', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  ) : (
                    ad.campaignExternalCRMId || '-'
                  )}
                </TableCell>
                <TableCell>
                  {editingRow?.id === ad.id ? (
                    <TextField
                      value={editingRow.productOfInterest}
                      onChange={(e) => handleEditingFieldChange('productOfInterest', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  ) : (
                    ad.productOfInterest || '-'
                  )}
                </TableCell>
                <TableCell>
                  {editingRow?.id === ad.id ? (
                    <>
                      <IconButton
                        onClick={() => {
                          handleSaveEdit();
                        }}
                        size="small"
                        color="primary"
                        disabled={isUpdating}
                      >
                        {isUpdating ? <CircularProgress size={16} /> : <SaveIcon />}
                      </IconButton>
                      <IconButton onClick={handleCancelEdit} size="small" disabled={isUpdating}>
                        <CancelIcon />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton onClick={() => handleEditClick(ad)} size="small" disabled={isDeleting}>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteAd(ad.id)}
                        size="small"
                        color="error"
                        disabled={isDeleting}
                      >
                        {isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filteredAds?.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {searchQuery.trim() ? `No ads found matching "${searchQuery}"` : 'No business unit ads found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default BusinessUnitAds;
